import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InputText } from 'primereact/inputtext';
import SimpleBar from 'simplebar-react';
import BrandIcon from '../../../../shared/components/brand-icon/BrandIcon';
import { visibleDomains, type OwnedKeywordDomain } from './KeywordDomainsCell';
import { setKeywordDomain } from '../../../../shared/services/monitor/keywords/KeywordsService';
import { GET_KEYWORD_DOMAINS } from '../../../../shared/api/query/keyword/keys';
import '../brand-dialog/BrandList.scss';
import './KeywordDomainsCell.scss';

interface BrandDomainsEditorProps {
  projectId: number;
  /** Główna nazwa marki — do niej trafia nowo dodana domena. */
  normalizedKeyword: string;
  /** Domeny wszystkich nazw marki (główna + warianty). */
  domains: OwnedKeywordDomain[];
}

/** Separatory wklejonej listy — każda domena zapisuje się osobno. */
const SEPARATORS = /[\s,;]+/;

/**
 * Domeny marki — zakładka „Domeny marki” w oknie marki: pole z przyciskiem „Dodaj” i przewijana lista.
 *
 * Oba zapisy są LOKALNE dla projektu (`monitor_keyword.included_domains` / `excluded_domains`) — słownik
 * globalny zapisuje wyłącznie system. „Usuń” to wykluczenie w tym projekcie, więc wykluczonych domen nie
 * pokazujemy; ponowne dodanie zdejmuje wykluczenie. Przycisk wysyła właściciela domeny (`owner`), bo przy
 * połączonej marce domena może należeć do wariantu, a wykluczenie siedzi przy nazwie, która ją ma.
 *
 * Lista główna to domeny potwierdzone w projekcie (`visibleDomains`); reszta słownika — zwykle inne firmy
 * o tej nazwie — siedzi zwinięta pod spodem, skąd „+” dopisuje domenę do marki (to samo co ręczne dodanie).
 */
export const BrandDomainsEditor: React.FC<BrandDomainsEditorProps> = ({
  projectId,
  normalizedKeyword,
  domains,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [error, setError] = useState(false);
  const [othersOpen, setOthersOpen] = useState(false);
  const { shown: visible, confirmed } = visibleDomains(domains);
  const others = confirmed
    ? domains.filter((domain) => !domain.isExcluded && !domain.isConfirmed)
    : [];

  const save = useMutation({
    mutationFn: (variables: { owner: string; domain: string; isExcluded: boolean }) =>
      setKeywordDomain({
        projectId,
        normalizedKeyword: variables.owner,
        domain: variables.domain,
        isExcluded: variables.isExcluded,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [GET_KEYWORD_DOMAINS, projectId] });
    },
    onError: () => setError(true),
  });

  // Po kolei, nie równolegle — każdy zapis odświeża tę samą listę domen projektu. Nieudana domena
  // wraca do pola do poprawki.
  const add = async () => {
    const values = draft.split(SEPARATORS).filter(Boolean);
    setDraft('');
    setError(false);
    for (const domain of values) {
      await save
        .mutateAsync({ owner: normalizedKeyword, domain, isExcluded: false })
        .catch(() => setDraft(domain));
    }
  };

  return (
    <div className="keyword-domains">
      <div className="keyword-domains__add">
        <InputText
          className={`keyword-domains__input${error ? ' form-box__invalid' : ''}`}
          placeholder={t('brandDomainAdd')}
          value={draft}
          onChange={(event) => {
            setDraft(event.target.value);
            setError(false);
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              void add();
            }
          }}
        />
        <button
          type="button"
          className="btn btn--sm"
          disabled={!draft.trim() || save.isPending}
          onClick={() => void add()}
        >
          <span className="ico ico--plus ico--xxs" aria-hidden="true" />
          {t('brandDomainAddSubmit')}
        </button>
      </div>
      {error && <span className="keyword-domains__error">{t('brandDomainInvalid')}</span>}
      {!confirmed && visible.length > 0 && (
        <span className="keyword-domains__note">{t('brandDomainsUnconfirmed')}</span>
      )}

      <div className="brand-list">
        <SimpleBar style={{ maxHeight: 264 }} autoHide={false}>
          {visible.length === 0 ? (
            <div className="brand-list__empty">{t('brandDomainsEmpty')}</div>
          ) : (
            visible.map((domain) => (
              <div key={domain.domain} className="brand-list__row">
                <BrandIcon name={domain.domain} domain={domain.domain} size="xs" />
                <span className="brand-list__name">
                  <span className="brand-list__name-text">{domain.domain}</span>
                </span>
                <button
                  type="button"
                  className="brand-list__icon-action brand-list__icon-action--danger"
                  title={t('brandDomainExclude')}
                  aria-label={t('remove')}
                  disabled={save.isPending}
                  onClick={() =>
                    save.mutate({ owner: domain.owner, domain: domain.domain, isExcluded: true })
                  }
                >
                  <span className="ico ico--trash ico--xs" aria-hidden="true" />
                </button>
              </div>
            ))
          )}
        </SimpleBar>
      </div>

      {others.length > 0 && (
        <div className="keyword-domains__others">
          <button
            type="button"
            className="keyword-domains__toggle"
            aria-expanded={othersOpen}
            onClick={() => setOthersOpen((open) => !open)}
          >
            <span className={`ico ico--${othersOpen ? 'top' : 'bottom'} ico--xxs`} aria-hidden="true" />
            {t('brandDomainsOthers', { count: others.length })}
          </button>
          {othersOpen && (
            <div className="brand-list">
              {others.map((domain) => (
                <div key={domain.domain} className="brand-list__row">
                  <BrandIcon name={domain.domain} domain={domain.domain} size="xs" />
                  <span className="brand-list__name">
                    <span className="brand-list__name-text">{domain.domain}</span>
                  </span>
                  <button
                    type="button"
                    className="brand-list__icon-action"
                    title={t('brandDomainConfirm')}
                    aria-label={t('brandDomainConfirm')}
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate({ owner: domain.owner, domain: domain.domain, isExcluded: false })
                    }
                  >
                    <span className="ico ico--plus ico--xs" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="brand-list__icon-action brand-list__icon-action--danger"
                    title={t('brandDomainExclude')}
                    aria-label={t('remove')}
                    disabled={save.isPending}
                    onClick={() =>
                      save.mutate({ owner: domain.owner, domain: domain.domain, isExcluded: true })
                    }
                  >
                    <span className="ico ico--trash ico--xs" aria-hidden="true" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BrandDomainsEditor;
